/**
 * FAQ accordion component. Renders an array of { question, answer }
 * pairs as independent expand/collapse items.
 *
 * The component is presentation-only — caller passes in already-parsed
 * Q/A data (either hand-coded array or splitFaqBody() output from a
 * markdown source). This separation lets the same component back the
 * legacy /faq page AND the new /p/faq CMS page during the transition.
 *
 * Visual spec preserved from the original FaqPage (0.32.23 → 0.32.27):
 *   - Closed: white bg, 1px #DCDCDC border, 2px radius, + icon
 *   - Open: bg blue-100, − icon, answer reveals below joined flush
 *     to the question with sharp top corners
 *   - Focus persists until user clicks elsewhere
 *   - Independent toggles (opening one doesn't close others)
 *   - Each answer rendered with MarkdownContent (supports paragraphs,
 *     bold, links, etc. — same parser as article-type pages)
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import MarkdownContent from './MarkdownContent';
import { rangesFor, setHighlightRanges } from '../lib/searchHighlight';
import styles from './FaqAccordion.module.css';

/* 0.45.29 - Plain text of a Markdown answer for searching: link
 * addresses, {: ...} suffixes and the formatting signs are left out, so
 * a search for "http" or "**" does not match every answer. */
function searchText(md) {
  return String(md || '')
    .replace(/\{:[^}]*\}/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`#>|]/g, ' ')
    .toLowerCase();
}

/* 0.45.29 - The FAQ's search (a linked Search element), as a table's:
 * the items whose question or answer contains the text, ignoring case.
 * Empty text: every item, or none when emptyShowsNone. Each kept item
 * carries `_key`, its position in the full list, so a question that is
 * open stays open while the list narrows and widens. */
export function filterFaqItems(items, query, emptyShowsNone = false) {
  const list = (Array.isArray(items) ? items : []).map((it, i) => ({ ...it, _key: i }));
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return emptyShowsNone ? [] : list;
  return list.filter(it => String(it.question || '').toLowerCase().includes(q)
    || searchText(it.answer).includes(q));
}

/* 0.45.30 - Search highlight; since 0.45.33 the shared engine in
 * lib/searchHighlight.js (also used by tables and dropdowns). */

/* `highlight`: the search text. While it is not empty, every question
 * shown (they all match) opens and the matches are coloured with
 * `highlightColor` (a CSS colour, Warning light by default), for as
 * long as the search text is there (0.45.35: clicks and scrolls no
 * longer remove it). */
export default function FaqAccordion({ items, fill = false, highlight = '', highlightColor = null, colorMatches = true }) {
  const [openIds, setOpenIds] = useState(new Set());
  const openIdsRef = useRef(openIds);
  openIdsRef.current = openIds;
  // 0.45.32 - What was open before the search started (null: no search
  // running), and the questions the visitor clicked during it.
  const beforeRef = useRef(null);
  const touchedRef = useRef(new Set());
  const listRef = useRef(null);
  const instance = useRef({});
  const q = String(highlight || '').trim();
  const [marksOn, setMarksOn] = useState(false);
  const shownIds = (items || []).map((item, i) => `q${item && item._key != null ? item._key : i}`).join('|');

  // 0.45.32 - The search and the open questions:
  //   search starts   remember what is open;
  //   while searching only the questions found are open (every other
  //                   one is closed), and the matches are coloured;
  //   search cleared  what was open before opens again, plus any
  //                   question the visitor clicked or selected text in
  //                   during the search that is still open.
  useEffect(() => {
    if (q) {
      if (beforeRef.current == null) {
        beforeRef.current = new Set(openIdsRef.current);
        touchedRef.current = new Set();
      }
      setOpenIds(new Set(shownIds.split('|').filter(Boolean)));
      setMarksOn(true);
      return;
    }
    setMarksOn(false);
    if (beforeRef.current != null) {
      const restored = new Set(beforeRef.current);
      touchedRef.current.forEach(id => { if (openIdsRef.current.has(id)) restored.add(id); });
      beforeRef.current = null;
      touchedRef.current = new Set();
      setOpenIds(restored);
    }
  }, [q, shownIds]);
  // A click on a question, or a click or text selection in an answer,
  // during a search: the visitor is reading it, so it stays open after.
  const touch = (id) => { if (beforeRef.current != null) touchedRef.current.add(id); };

  // 0.45.35 - The colour stays while there is search text (clicks and
  // scrolls no longer remove it).

  // Paint the matches after every render that may move them.
  useLayoutEffect(() => {
    // 0.45.31 - colorMatches false ("Highlight matches" unticked): the
    // questions still open, nothing is coloured.
    setHighlightRanges(instance.current,
      marksOn && q && colorMatches ? rangesFor(listRef.current, q) : null);
  });
  useEffect(() => () => setHighlightRanges(instance.current, null), []);

  const toggle = (id) => {
    touch(id);
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!items || items.length === 0) return null;

  return (
    <ul ref={listRef} className={fill ? `${styles.list} ${styles.fill}` : styles.list}
        style={highlightColor ? { '--search-highlight-color': highlightColor } : undefined}>
      {items.map((item, i) => {
        const id = `q${item && item._key != null ? item._key : i}`;
        const answerId = `faq-answer-${id}`;
        const isOpen = openIds.has(id);
        return (
          <li key={id} className={styles.item}>
            <button
              type="button"
              className={`${styles.question} ${isOpen ? styles.questionOpen : ''}`}
              aria-expanded={isOpen}
              aria-controls={answerId}
              onClick={() => toggle(id)}
            >
              <span className={styles.icon} aria-hidden="true">
                {isOpen ? '−' : '+'}
              </span>
              <span className={styles.questionText}>{item.question}</span>
            </button>
            <div id={answerId} className={styles.answer} hidden={!isOpen}
                 onMouseDown={() => touch(id)} onTouchStart={() => touch(id)}>
              <MarkdownContent source={item.answer} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
